<template>
  <div class="pa-4">
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <v-card-title class="pa-0">Análisis de Errores</v-card-title>
        <v-btn-toggle v-model="selectedDays" mandatory dense color="red darken-1">
          <v-btn :value="7" small>7 días</v-btn>
          <v-btn :value="15" small>15 días</v-btn>
          <v-btn :value="30" small>30 días</v-btn>
          <v-btn :value="90" small>90 días</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <v-row v-if="loadingErrors" class="fill-height" align-content="center" justify="center">
      <v-col cols="6">
        <v-col
          class="text-subtitle-1 text-center"
          cols="12"
        >
          Consultando información por favor espere...
        </v-col>
        <v-progress-linear color="red darken-4" indeterminate rounded height="6" />
      </v-col>
    </v-row>

    <template v-if="errorsAnalysis && !loadingErrors">
      
      <v-row>
        
        <v-col cols="12" md="7">
          <base-card>
            <v-card-text>
              <v-card-title>
                Errores por Proceso y Estado
                <v-spacer />
              </v-card-title>
              <apexchart
                v-if="processStatusChartSeries.length"
                type="bar"
                height="300"
                :options="processStatusChartOptions"
                :series="processStatusChartSeries"
              />
              <div v-else class="text-center grey--text py-8">
                No se encontraron errores en este período
              </div>
            </v-card-text>
          </base-card>
        </v-col>

        
        <v-col cols="12" md="5">
          <base-card>
            <v-card-text>
              <v-card-title>
                Errores por Compañía
                <v-spacer />
              </v-card-title>
              <v-data-table
                :headers="companyHeaders"
                :items="errorsAnalysis.by_company || []"
                class="elevation-1"
                @click:row="goToCompanyErrors"
                style="cursor: pointer"
              >
                <template v-slot:[`item.total`]="{ item }">
                  <v-chip color="red" dark small>{{ item.total }}</v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </base-card>
        </v-col>
      </v-row>

      
      <v-row class="mt-4">
        <v-col cols="12">
          <base-card>
            <v-card-text>
              <v-card-title>
                Errores Recientes
                <v-spacer />
              </v-card-title>
              <v-data-table
                :headers="recentErrorHeaders"
                :items="errorsAnalysis.recent_errors || []"
                class="elevation-1"
                @click:row="goToDetail"
                style="cursor: pointer"
              >
                <template v-slot:[`item.final_status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.final_status)"
                    dark
                    small
                    label
                  >
                    <v-icon left x-small>{{ getStatusIcon(item.final_status) }}</v-icon>
                    {{ item.final_status }}
                  </v-chip>
                </template>

                <template v-slot:[`item.source_process`]="{ item }">
                  {{ getProcessLabel(item.source_process) }}
                </template>

                <template v-slot:[`item.total_duration_ms`]="{ item }">
                  {{ formatDuration(item.total_duration_ms) }}
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>

                <template v-slot:[`item.consecutive`]="{ item }">
                  {{ item.prefix }}-{{ item.consecutive }}
                </template>
              </v-data-table>
            </v-card-text>
          </base-card>
        </v-col>
      </v-row>
    </template>

    <v-alert v-if="error && !loadingErrors" type="error" dismissible class="mt-4">
      {{ error }}
    </v-alert>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatDuration, getStatusColor, getStatusIcon, getProcessLabel } from '@/data/wideEventsStatuses'

export default {
  data () {
    return {
      selectedDays: 7,
      companyHeaders: [
        { text: 'ID', value: 'company_id', align: 'center' },
        { text: 'Compañía', value: 'company_name', align: 'center' },
        { text: 'Total Errores', value: 'total', align: 'center' },
      ],
      recentErrorHeaders: [
        { text: 'Estado', value: 'final_status', align: 'center', width: '120px' },
        { text: 'Prefijo-Consecutivo', value: 'consecutive', align: 'center' },
        { text: 'Compañía', value: 'company_name', align: 'center' },
        { text: 'Proceso', value: 'source_process', align: 'center' },
        { text: 'Duración', value: 'total_duration_ms', align: 'center' },
        { text: 'Fecha', value: 'created_at', align: 'center' },
      ],
    }
  },
  computed: {
    ...mapState('wideEvents', ['errorsAnalysis', 'loadingErrors', 'error']),
    processStatusChartSeries () {
      if (!this.errorsAnalysis || !this.errorsAnalysis.by_process_and_status) return []

      const data = this.errorsAnalysis.by_process_and_status
      const statuses = [...new Set(data.map(d => d.final_status))]

      return statuses.map(status => ({
        name: status,
        data: this.getProcesses().map(process => {
          const found = data.find(d => d.source_process === process && d.final_status === status)
          return found ? found.total : 0
        }),
      }))
    },
    processStatusChartOptions () {
      return {
        chart: { stacked: true },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
        xaxis: {
          categories: this.getProcesses().map(p => getProcessLabel(p)),
        },
        colors: ['#F44336', '#FF9800', '#2196F3', '#4CAF50'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      }
    },
  },
  watch: {
    selectedDays () {
      this.loadErrors()
    },
  },
  created () {
    this.loadErrors()
  },
  methods: {
    formatDuration,
    getStatusColor,
    getStatusIcon,
    getProcessLabel,
    loadErrors () {
      this.$store.dispatch('wideEvents/getErrorsAnalysis', this.selectedDays)
    },
    getProcesses () {
      if (!this.errorsAnalysis || !this.errorsAnalysis.by_process_and_status) return []
      return [...new Set(this.errorsAnalysis.by_process_and_status.map(d => d.source_process))]
    },
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleString('es-EC', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    goToDetail (item) {
      this.$router.push({
        name: 'wideEventDetail',
        params: { traceId: item.trace_id },
      })
    },
    goToCompanyErrors (item) {
      this.$router.push({
        name: 'wideEventsSearch',
        query: { company_id: item.company_id, has_errors: 'true' },
      })
    },
  },
}
</script>
